

/**
 * Gating Level Values:
 * Contains possible gating level values.
 * The gating level is the initial gating value, but does not take into account other application states like user cookies, or known visitor status.
 * Use the GatingState module instead if you need to take application state into account.
 * The gating level values are slightly different in different areas of the application.
 * - GatingLevel.UserCookie: possible gating levels when writing the user gating cookie.
 * - GatingLevel.MarketoForm: possible gating levels for marketo form components.
 * - GatingLevel.Resource: possible gating levels for gated resources
 */

export namespace GatingLevel {
  /** Gating level values for Marketo forms */
  export const MarketoForm = Object.freeze(["FullGate", "PartialGate"] as const);
  export type MarketoForm = typeof MarketoForm[number];

  /** Gating level values for form experiences, includes "NoGate" where GatingLevel.MarketoForm does not, since a form will never display when the state is "NoGate" */
  export const FormExperience = Object.freeze([...GatingLevel.MarketoForm, "NoGate"] as const);
  export type FormExperience = typeof FormExperience[number];

  /** Gating level values for gated resources */
  export namespace Resource {
    /** The actual value stored in the resource `gatingLevel` field. The "Ungated" value here is the same as the "NoGate" experience, which is what the value is after parsing. */
    export const Raw = Object.freeze(["Full Gate", "Partial Gate", "Ungated"] as const);
    export type Raw = typeof Raw[number];

    /** The gating level value returned after parsing the resources in a form experience */
    export const Parsed = GatingLevel.FormExperience;
    export type Parsed = typeof Parsed[number];
  }

  /** Gating Level values that can be stored on the user gating cookie */
  export const UserCookie = GatingLevel.MarketoForm;
  export type UserCookie = GatingLevel.MarketoForm;
}

export type GatingLevelFor<TFormOrExperience extends "MarketoForm" | "FormExperience"> =
  TFormOrExperience extends "MarketoForm"
    ? GatingLevel.MarketoForm
    :
  TFormOrExperience extends "FormExperience"
    ? GatingLevel.FormExperience
    : never;